import { Container } from '@/components/ui';
import {
  FREQUENCY_OPTIONS,
  ScanFrequency,
} from '@/sections/settings/subscription/constant';
import { cn } from '@/utils/classname';

export interface ScanningFrequencySelectorProps {
  selectedFrequency: ScanFrequency;
  onFrequencyChange: (frequency: ScanFrequency) => void;
  disabled?: boolean;
  className?: string;
}

/**
 * ScanningFrequencySelector - Reusable component for scanning frequency selection
 * Extracted from configure/index.tsx for use in both settings and wizard
 */
export function ScanningFrequencySelector({
  selectedFrequency,
  onFrequencyChange,
  disabled = false,
  className,
}: ScanningFrequencySelectorProps) {
  return (
    <section
      className={cn('space-y-2', className)}
      aria-labelledby="frequency-section-title"
    >
      <h3
        id="frequency-section-title"
        className="text-chariot-text-primary text-sm font-semibold"
      >
        Scanning Frequency
      </h3>
      <div
        className="grid grid-cols-1 gap-3 sm:grid-cols-4"
        role="radiogroup"
        aria-labelledby="frequency-section-title"
        aria-describedby="frequency-description"
      >
        <p id="frequency-description" className="sr-only">
          Choose how often security scans should be performed. Higher frequency
          increases pricing.
        </p>
        {FREQUENCY_OPTIONS.map(option => {
          const isSelected = selectedFrequency === option.value;

          return (
            <Container
              key={option.value}
              onClick={() =>
                !disabled && onFrequencyChange(option.value as ScanFrequency)
              }
              className={cn(
                'relative cursor-pointer py-2 text-center',
                disabled && 'cursor-not-allowed opacity-50'
              )}
              role="radio"
              isSelected={isSelected}
              aria-checked={isSelected}
              aria-describedby={`frequency-${option.value}-desc`}
              data-testid={`frequency-${option.value}`}
            >
              {isSelected && (
                <div className="bg-chariot-brand-primary absolute -top-2.5 left-1/2 -translate-x-1/2 transform rounded-full px-2 py-0.5 text-[10px] font-bold text-white">
                  YOUR PLAN
                </div>
              )}
              <span className="text-sm font-medium">{option.label}</span>
            </Container>
          );
        })}
      </div>
    </section>
  );
}
